import "katex/dist/katex.min.css";

import { Box, CircularProgress, Paper, Typography, useTheme } from "@mui/material";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

interface RichChatMessageProps {
  text: string;
  isUser: boolean;
  isStreaming?: boolean;
}

export const RichChatMessage = ({ text, isUser, isStreaming }: RichChatMessageProps) => {
  const theme = useTheme();

  const components: Components = {
    a({ href, children }) {
      return (
        <Typography
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            "&:hover": {
              textDecoration: "none",
            },
            color: theme.palette.primary.main,
            textDecoration: "underline",
          }}
        >
          {children}
        </Typography>
      );
    },

    blockquote({ children }) {
      return (
        <Paper
          sx={{
            backgroundColor: theme.palette.action.hover,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            fontStyle: "italic",
            margin: theme.spacing(1, 0),
            padding: theme.spacing(1, 2),
          }}
        >
          {children}
        </Paper>
      );
    },
    em({ children }) {
      return (
        <Typography component="em" sx={{ fontStyle: "italic" }}>
          {children}
        </Typography>
      );
    },
    h1({ children }) {
      return (
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", mb: 1 }}>
          {children}
        </Typography>
      );
    },

    h2({ children }) {
      return (
        <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mb: 1 }}>
          {children}
        </Typography>
      );
    },
    h3({ children }) {
      return (
        <Typography variant="h6" component="h3" sx={{ fontWeight: "bold", mb: 1 }}>
          {children}
        </Typography>
      );
    },
    hr() {
      return (
        <Box
          component="hr"
          sx={{
            backgroundColor: theme.palette.divider,
            border: "none",
            height: "1px",
            margin: theme.spacing(2, 0),
          }}
        />
      );
    },

    li({ children }) {
      return (
        <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
          {children}
        </Typography>
      );
    },
    ol({ children }) {
      return (
        <Box component="ol" sx={{ my: 1, pl: 2 }}>
          {children}
        </Box>
      );
    },
    p({ children }) {
      return (
        <Typography
          variant="body1"
          component="p"
          sx={{
            "&:not(:last-child)": {
              marginBottom: theme.spacing(1),
            },
            margin: 0,
          }}
        >
          {children}
        </Typography>
      );
    },
    strong({ children }) {
      return (
        <Typography component="strong" sx={{ fontWeight: "bold" }}>
          {children}
        </Typography>
      );
    },
    table({ children }) {
      return (
        <Box sx={{ my: 2, overflowX: "auto" }}>
          <Box
            component="table"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderCollapse: "collapse",
              borderRadius: theme.spacing(1),
              width: "100%",
            }}
          >
            {children}
          </Box>
        </Box>
      );
    },
    tbody({ children }) {
      return <Box component="tbody">{children}</Box>;
    },

    td({ children }) {
      return (
        <Typography
          component="td"
          variant="body2"
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1),
          }}
        >
          {children}
        </Typography>
      );
    },

    th({ children }) {
      return (
        <Typography
          component="th"
          variant="body2"
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            fontWeight: "bold",
            padding: theme.spacing(1),
            textAlign: "left",
          }}
        >
          {children}
        </Typography>
      );
    },

    thead({ children }) {
      return (
        <Box
          component="thead"
          sx={{
            backgroundColor: theme.palette.action.hover,
          }}
        >
          {children}
        </Box>
      );
    },

    tr({ children }) {
      return (
        <Box
          component="tr"
          sx={{
            "&:nth-of-type(even)": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {children}
        </Box>
      );
    },

    ul({ children }) {
      return (
        <Box component="ul" sx={{ my: 1, pl: 2 }}>
          {children}
        </Box>
      );
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
      }}
    >
      <Paper
        sx={{
          backgroundColor: isUser ? "primary.main" : "grey.100",
          borderRadius: 2,
          color: isUser ? "primary.contrastText" : "text.primary",
          maxWidth: "70%",
          p: 2,
          ...(isUser && {
            borderBottomRightRadius: 4,
          }),
          ...(!isUser && {
            borderBottomLeftRadius: 4,
          }),
          ...(isUser && {
            "& .katex": {
              color: theme.palette.primary.contrastText,
            },
            "& code": {
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
            },
          }),
        }}
      >
        {text.trim() ? (
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={components}>
            {text}
          </ReactMarkdown>
        ) : isStreaming ? (
          <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body1" color="text.secondary">
              AI is typing...
            </Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};
